import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import Swal from 'sweetalert2';

import { ProposalDeliverableForm } from '@teem/model-forms/proposal-deliverable-form';
import { ProposalDeliverable } from '@teem/models/proposal-deliverable';
import { messages as i18n } from '@teem/constants/messages';
import { client as ClientApi } from '@teem/constants/api';
import { UserService } from '@teem/services/user.service';
import { parseDate } from '@teem/helpers/functions';
import { Proposal } from '@teem/models/proposal';

import { PaymentInfoModalComponent } from '@teem/proposals-module/modals/payment-info-modal/payment-info-modal.component';



@Component({
  selector: 'edit-proposal-quotes',
  templateUrl: './quotes.component.html',
  styles: []
})
export class QuotesComponent implements OnInit {
  @Input() proposal: Proposal;
  @Output() skipStep: EventEmitter<any> = new EventEmitter();
  @Output() triggerPrevious: EventEmitter<any> = new EventEmitter();
  @Output() onComplete: EventEmitter<any> = new EventEmitter();
  quoteForm: FormGroup;
  deliverableForm: FormGroup;
  deliverables: Array<any> = [];
  isDeliverableSubmitted: boolean = false;
  quoteSubmitted: boolean = false;
  projectTypes: object[] = [
    { value: 'fixed', 'label': $localize`:@@ts.common.fixed:Fixed` },
    { value: 'hourly', 'label': $localize`:@@ts.common.hourly:Hourly` },
  ];
  defaultPaymentInfo: any = {};
  clientListApi: string = ClientApi.list;
  selectedClient: any;

  @Input('client')
  set setClient(value: any) {
    if (value) {
      this.selectedClient = value;
      this.quoteForm.get('client_id').setValue(value.id);
    }
  }

  @Input('defaultPaymentInfo')
  set setDefaultPaymentInfo(value: any) {
    this.defaultPaymentInfo = value;
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private userService: UserService) { }

  ngOnInit(): void {
    this.quoteForm = this.fb.group({
      proposal_number: [Math.floor(Math.random() * 100), [Validators.required]],
      quote_date: [moment().format('DD/MM/YYYY'), [Validators.required]],
      project_type: ['fixed', [Validators.required]],
      total_amount: [0],
      include_tax: [false],
      tax_rate: [],
      payment_info: [{}],
      client_id: [null, [Validators.required]],
      save_default_payment_info: [false]
    })

    if (this.proposal) {
      this.quoteForm.get('proposal_number').setValue(this.proposal.proposal_number)

      this.quoteForm.patchValue({
        quote_date: moment(this.proposal.quote_date).format('DD/MM/YYYY'),
        project_type: this.proposal.project_type ? this.proposal.project_type : 'fixed',
        total_amount: this.proposal.total_amount,
        include_tax: this.proposal.tax_rate ? true : false,
        tax_rate: this.proposal.tax_rate,
        payment_info: this.proposal.payment_info,
        save_default_payment_info: [false]
      })
    }

    if (this.proposal.deliverables) {
      this.deliverables = this.proposal.deliverables;
    }

    this.deliverableForm = this.fb.group(new ProposalDeliverableForm(new ProposalDeliverable()));
  }

  selectClient(val: any) {
    if (val.id && val.text) {
      this.selectedClient = {
        id: val.id,
        full_name: val.text,
        email: val.email
      }
    }
  }

  get totalAmount() {
    let items = this.deliverables ? this.deliverables : [];
    if (items && items.length > 0) {
      return items.reduce((a, b) => a + this.deliverableCost(b), 0);
    }
    return 0;
  }

  get subTotal() {
    if (this.totalAmount) {

      let taxPercent = 0;
      if (this.quoteForm.get('include_tax').value)
        taxPercent = this.quoteForm.get('tax_rate').value;

      return taxPercent ? (this.totalAmount + this.totalAmount * taxPercent / 100) : this.totalAmount;
    }

    return 0;
  }

  enableTax(value: boolean) {
    let taxPercentControl = this.quoteForm.get('tax_rate');
    if (value) {
      taxPercentControl.setValidators([Validators.required]);
    } else {
      taxPercentControl.setValue(null);
      taxPercentControl.clearValidators();
    }
    taxPercentControl.updateValueAndValidity();
  }

  deliverableCost(formValues) {
    if (formValues.rate && formValues.quantity) {
      return formValues.rate * formValues.quantity;
    }
    return 0;
  }

  addDeliverable(form: FormGroup) {
    this.isDeliverableSubmitted = true;
    if (form.valid) {
      this.deliverables.push(form.value);
      this.isDeliverableSubmitted = false;
      form.reset({});
      document.getElementById('create-deliverable').querySelector('input').focus();
    }
  }

  removeDeliverable(index: number) {
    this.deliverables.splice(index, 1);
  }


  openAddPaymentInfoModal() {
    let paymentInfo = this.quoteForm.get('payment_info').value;
    if (!paymentInfo || !paymentInfo.name) {
      paymentInfo = this.defaultPaymentInfo
    }

    const modalRef = this.modalService.open(PaymentInfoModalComponent);
    modalRef.componentInstance.paymentInfo = paymentInfo;

    modalRef.result.then(result => {
      this.quoteForm.get('payment_info').setValue(result.formData);
      this.quoteForm.get('save_default_payment_info').setValue(result.submit_type == "default");
    }, fallbackResult => {
      // do nothing
    })

  }

  submit() {
    this.quoteSubmitted = true;
    if (this.quoteForm.valid) {
      if (this.deliverables.length == 0) {
        Swal.fire({
          title: i18n.warning,
          text: i18n.proposal.please_add_deliverable,
          icon: 'warning'
        })
        return;
      }

      this.isDeliverableSubmitted = false;
      this.quoteSubmitted = false;


      let fd = Object.assign({}, this.quoteForm.value);
      fd.deliverables = this.deliverables;
      fd.total_amount = this.totalAmount;
      fd.quote_date = parseDate(fd.quote_date);
      fd.sub_total = this.subTotal;

      fd.client = this.selectedClient;

      this.onComplete.emit(fd);
    }
  }
}
